import { API_URL } from './utils';
import { useAuthStore, type AuthTokens, type AuthUser } from '@/stores/auth.store';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public override readonly message: string,
    public readonly body?: unknown,
  ) {
    super(message);
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

let refreshPromise: Promise<AuthTokens> | null = null;

async function refreshAccessToken(): Promise<AuthTokens> {
  if (refreshPromise) return refreshPromise;
  const { tokens, clear, setTokens } = useAuthStore.getState();
  if (!tokens?.refreshToken) {
    clear();
    throw new ApiError(401, 'Sessão expirada');
  }

  refreshPromise = fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) {
        clear();
        throw new ApiError(res.status, 'Falha ao renovar sessão');
      }
      const data = (await res.json()) as AuthTokens & { expiresIn: number };
      const next = { accessToken: data.accessToken, refreshToken: data.refreshToken };
      setTokens(next);
      return next;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { tokens } = useAuthStore.getState();
  const headers = new Headers(opts.headers);
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  if (!opts.skipAuth && tokens?.accessToken) {
    headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // 401 com refresh token disponível → tenta renovar 1 vez e refaz a chamada
  if (res.status === 401 && !opts.skipAuth && !opts.skipRefresh && tokens?.refreshToken) {
    try {
      await refreshAccessToken();
      return api<T>(path, { ...opts, skipRefresh: true });
    } catch {
      throw new ApiError(401, 'Sessão expirada');
    }
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const message =
      (body as { message?: string | string[] })?.message instanceof Array
        ? ((body as { message: string[] }).message ?? []).join(', ')
        : ((body as { message?: string })?.message ?? `Erro ${res.status}`);
    throw new ApiError(res.status, message, body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------- Helpers tipados ----------

export interface LoginResponse extends AuthTokens {
  expiresIn: number;
}

/** Desafio de MFA devolvido por /auth/login quando o usuário tem 2º fator ativo. */
export interface MfaChallenge {
  mfaRequired: true;
  mfaToken: string;
}

/** Resposta de /auth/login: ou tokens emitidos, ou desafio de MFA. */
export type LoginResult = ({ mfaRequired: false } & LoginResponse) | MfaChallenge;

export const authApi = {
  login: (email: string, password: string) =>
    api<LoginResult>('/auth/login', { method: 'POST', body: { email, password }, skipAuth: true }),
  loginMfa: (mfaToken: string, code: string) =>
    api<LoginResponse>('/auth/login/mfa', {
      method: 'POST',
      body: { mfaToken, code },
      skipAuth: true,
    }),
  me: () => api<AuthUser>('/auth/me'),
  logout: (refreshToken: string) =>
    api<void>('/auth/logout', { method: 'POST', body: { refreshToken } }),
};

// ---------- MFA self-service (perfil do próprio usuário) ----------

export interface MfaStatus {
  enabled: boolean;
  enrolledAt: string | null;
  recoveryCodesRemaining: number;
}

export interface MfaSetupResult {
  otpauthUrl: string;
  qrcodeDataUrl: string;
  secret: string;
}

export interface MfaRecoveryCodes {
  recoveryCodes: string[];
}

export interface MfaProofInput {
  code?: string;
  password?: string;
}

export const mfaApi = {
  status: () => api<MfaStatus>('/auth/me/mfa'),
  setup: () => api<MfaSetupResult>('/auth/me/mfa/setup', { method: 'POST' }),
  enable: (code: string) =>
    api<MfaRecoveryCodes>('/auth/me/mfa/enable', { method: 'POST', body: { code } }),
  disable: (proof: MfaProofInput) =>
    api<void>('/auth/me/mfa/disable', { method: 'POST', body: proof }),
  regenerateRecoveryCodes: (proof: MfaProofInput) =>
    api<MfaRecoveryCodes>('/auth/me/mfa/recovery-codes', { method: 'POST', body: proof }),
  /** Reset administrativo do MFA de outro usuário (ADMIN). */
  adminReset: (userId: string) =>
    api<{ ok: true }>(`/users/${userId}/mfa/reset`, { method: 'POST' }),
};
